import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";
import NavbarCliente from "../components/NavbarCliente";
import FooterCliente from "../components/FooterCliente";
import ProductoCard from "../components/ProductoCard";
import CarritoCliente from "../components/CarritoCliente";
import WhatsappButton from "../components/WhatsappButtonTracked";
import { ensureAnalyticsResumen, trackVisit, trackWhatsappClick } from "../services/analyticsService";
import { FaHome } from "react-icons/fa";

// 🔹 Firebase Storage
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { Carousel } from "react-bootstrap";

function CategoriaTextoAjustado({ texto }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(16);

  useEffect(() => {
    const ajustarFuente = () => {
      const container = containerRef.current;
      const text = textRef.current;

      if (!container || !text) return;

      let nextFontSize = 16;
      text.style.fontSize = `${nextFontSize}px`;

      const maxWidth = container.clientWidth;
      const maxHeight = container.clientHeight;

      while (
        nextFontSize > 10 &&
        (text.scrollWidth > maxWidth || text.scrollHeight > maxHeight)
      ) {
        nextFontSize -= 1;
        text.style.fontSize = `${nextFontSize}px`;
      }

      setFontSize(nextFontSize);
    };

    ajustarFuente();

    const observer = new ResizeObserver(() => {
      ajustarFuente();
    });

    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [texto]);

  return (
    <span
      ref={containerRef}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <span
        ref={textRef}
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          lineHeight: 1.1,
          wordBreak: "break-word",
          fontSize: `${fontSize}px`,
        }}
      >
        {texto}
      </span>
    </span>
  );
}

function CategoriaBadge({ cantidad }) {
  return (
    <span
      style={{
        minWidth: "26px",
        height: "26px",
        padding: "0 7px",
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255,255,255,0.16)",
        border: "1px solid rgba(255,255,255,0.22)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
        color: "#fff",
        fontSize: "0.78rem",
        fontWeight: 800,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {cantidad}
    </span>
  );
}

export default function EmpresaPage() {
  const { empresaSlug } = useParams();

  const [empresa, setEmpresa] = useState(null);

  // ✅ Info mínima para el loading
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [empresaLogoUrl, setEmpresaLogoUrl] = useState("");

  const [productos, setProductos] = useState([]);
  const [showCarrito, setShowCarrito] = useState(false);
  const [promociones, setPromociones] = useState([]);

  // ✅ Reserva (alquiler temporario)
  const [showReserva, setShowReserva] = useState(false);

  // 🔹 Filtros
  const [busqueda, setBusqueda] = useState("");
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("");
  const ordenPrecio = "asc";

  // ✅ NUEVO: pre front de categorías
  const [mostrarPreFront, setMostrarPreFront] = useState(true);

  // 🔹 Colores por defecto
  const [colores, setColores] = useState({
    fondo: "#f5f5f5",
    header: "red",
  });

  // ✅ Aplicar colores sin warnings
  const aplicarColores = useCallback((fondo, header) => {
    document.body.style.backgroundColor = fondo;
    document.documentElement.style.setProperty("--header-color", header);
    setColores({ fondo, header });
  }, []);

  // ✅ Normaliza teléfono para WhatsApp
  const normalizePhoneForWA = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("549")) return digits;
    if (digits.startsWith("54")) return `549${digits.slice(2)}`;
    const noZero = digits.startsWith("0") ? digits.slice(1) : digits;
    return `549${noZero}`;
  };

  // ✅ Abrir WhatsApp con mensaje de promos
  const abrirWhatsappPromos = useCallback(() => {
    const phone = normalizePhoneForWA(empresa?.telefono);
    if (!phone) return;

    const msg = "Quiero consultar por promos";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    trackWhatsappClick({
      empresaId: empresa?.id,
      empresaSlug: empresa?.empresaSlug || empresaSlug,
    }).catch((error) => {
      console.warn("No se pudo registrar el click de WhatsApp:", error);
    });
    window.open(url, "_blank", "noopener,noreferrer");
  }, [empresa?.id, empresa?.telefono, empresa?.empresaSlug, empresaSlug]);

  // ✅ Abrir WhatsApp con mensaje de reserva
  const abrirWhatsappReserva = useCallback(() => {
    const phone = normalizePhoneForWA(empresa?.telefono);
    if (!phone) return;

    const msg = `Hola ${empresa?.nombre || ""}, quiero reservar. ¿Me pasás disponibilidad?`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    trackWhatsappClick({
      empresaId: empresa?.id,
      empresaSlug: empresa?.empresaSlug || empresaSlug,
    }).catch((error) => {
      console.warn("No se pudo registrar el click de WhatsApp:", error);
    });
    window.open(url, "_blank", "noopener,noreferrer");
  }, [empresa?.id, empresa?.telefono, empresa?.nombre, empresa?.empresaSlug, empresaSlug]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const qEmp = query(collection(db, "empresas"), where("empresaSlug", "==", empresaSlug));
        const snapEmp = await getDocs(qEmp);

        if (snapEmp.empty) return;

        const empresaDoc = snapEmp.docs[0];
        const empresaData = { id: empresaDoc.id, ...empresaDoc.data() };

        if (mounted) {
          setEmpresaNombre(empresaData?.nombre || empresaSlug);
          setEmpresaLogoUrl(empresaData?.logoUrl || "");
        }

        if (empresaData?.colores) {
          aplicarColores(empresaData.colores.fondo, empresaData.colores.header);
        } else {
          aplicarColores(colores.fondo, colores.header);
        }

        const storage = getStorage();

        const productosPromise = (async () => {
          const qProd = query(collection(db, "productos"), where("empresaEmail", "==", empresaData.email));
          const snapProd = await getDocs(qProd);
          return snapProd.docs.map((d) => ({ id: d.id, ...d.data() }));
        })();

        const promosPromise = (async () => {
          try {
            const carpetaPromos = ref(storage, `promociones/${empresaData.nombre}`);
            const lista = await listAll(carpetaPromos);
            const urls = await Promise.all(lista.items.map((item) => getDownloadURL(item)));
            return urls;
          } catch (error) {
            console.warn("No hay promociones cargadas:", error);
            return [];
          }
        })();

        const [prods, promos] = await Promise.all([productosPromise, promosPromise]);

        if (!mounted) return;

        setProductos(prods);
        setPromociones(promos);
        setEmpresa(empresaData);

      } catch (e) {
        console.warn("Error cargando EmpresaPage:", e);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [empresaSlug, aplicarColores, colores.fondo, colores.header]);

  useEffect(() => {
    if (!empresa?.id || typeof window === "undefined") return;

    const visitKey = `nn_visit_${empresa.id}_${window.location.pathname}`;
    if (window.sessionStorage.getItem(visitKey)) return;

    const registerVisit = async () => {
      try {
        await ensureAnalyticsResumen(empresa.id);
        await trackVisit({
          empresaId: empresa.id,
          empresaSlug: empresa.empresaSlug || empresaSlug,
        });
        window.sessionStorage.setItem(visitKey, "1");
      } catch (error) {
        console.warn("No se pudo registrar la visita:", error);
      }
    };

    registerVisit();
  }, [empresa?.id, empresa?.empresaSlug, empresaSlug]);

  useEffect(() => {
    if (!empresa) return;

    document.title = `${empresa.nombre} | NegociosNet`;

    if (empresa.colores) {
      aplicarColores(empresa.colores.fondo, empresa.colores.header);
    } else {
      aplicarColores(colores.fondo, colores.header);
    }
  }, [empresa, aplicarColores, colores.fondo, colores.header]);

  // ✅ Bloqueo scroll cuando modal reserva abierto
  useEffect(() => {
    if (!showReserva) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showReserva]);

  // ✅ Layout cards
  const layoutCards = useMemo(() => {
    return empresa?.layoutCards === "1" ? "1" : "2";
  }, [empresa?.layoutCards]);

  const cardColClass = useMemo(() => {
    return layoutCards === "1"
      ? "col-12 d-flex justify-content-center"
      : "col-12 col-sm-6 col-lg-4 col-xl-3 d-flex justify-content-center";
  }, [layoutCards]);

  // ✅ Categorías
  const categorias = useMemo(() => {
    return [...new Set(productos.map((p) => p.categoria).filter(Boolean))];
  }, [productos]);

  const cantidadPorCategoria = useMemo(() => {
    return productos.reduce((acc, producto) => {
      if (!producto?.categoria) return acc;
      acc[producto.categoria] = (acc[producto.categoria] || 0) + 1;
      return acc;
    }, {});
  }, [productos]);

  const productosFiltrados = useMemo(() => {
    return productos.filter((p) => {
      const coincideNombre = (p.nombre || "").toLowerCase().includes(busqueda.toLowerCase());
      const coincideCategoria = categoriaSeleccionada ? p.categoria === categoriaSeleccionada : true;
      return coincideNombre && coincideCategoria;
    });
  }, [productos, busqueda, categoriaSeleccionada]);

  const handleSeleccionCategoria = (cat) => {
    setCategoriaSeleccionada(cat);
    setBusqueda("");
    setMostrarPreFront(false);
  };

  const volverACategorias = () => {
    setMostrarPreFront(true);
    setBusqueda("");
    setCategoriaSeleccionada("");
  };

  // ✅ LOADING
  if (!empresa) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center vh-100 text-center px-3"
        style={{
          background:
            "radial-gradient(circle at 20% 10%, rgba(255,43,220,0.18), transparent 55%)," +
            "radial-gradient(circle at 80% 30%, rgba(0,194,255,0.14), transparent 55%)," +
            "linear-gradient(180deg, #05050a 0%, #000 100%)",
          color: "#fff",
        }}
      >
        {empresaLogoUrl ? (
          <img
            src={empresaLogoUrl}
            alt="Logo"
            style={{
              width: 92,
              height: 92,
              borderRadius: 20,
              objectFit: "cover",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 18px 55px rgba(111,66,193,0.35)",
              marginBottom: 16,
            }}
          />
        ) : (
          <div
            style={{
              width: 92,
              height: 92,
              borderRadius: 20,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "0 18px 55px rgba(111,66,193,0.22)",
              marginBottom: 16,
            }}
          />
        )}

        <div className="nn-loader" />

        <div className="mt-4" style={{ maxWidth: 520 }}>
          <div
            style={{
              display: "inline-block",
              padding: "6px 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
              fontWeight: 800,
              letterSpacing: "0.3px",
              color: "#fff",
              marginBottom: 10,
            }}
          >
            🚀 Preparando tu tienda
          </div>

          <div style={{ fontSize: 18, opacity: 0.9 }}>Cargando los mejores productos de</div>

          <div
            style={{
              marginTop: 6,
              fontSize: 26,
              fontWeight: 900,
              lineHeight: 1.1,
              background: "linear-gradient(135deg, #6f42c1, #ff2bdc, #00c2ff)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textTransform: "capitalize",
              wordBreak: "break-word",
            }}
          >
            {empresaNombre || empresaSlug}
          </div>

          <div style={{ marginTop: 10, fontSize: 13, opacity: 0.75 }}>
            Un segundo… estamos armando una experiencia de compra rápida y segura.
          </div>
        </div>

        <style>{`
          .nn-loader{
            width: 86px;
            height: 86px;
            border-radius: 999px;
            border: 6px solid rgba(255,255,255,0.10);
            border-top-color: rgba(255,43,220,0.95);
            border-right-color: rgba(0,194,255,0.85);
            border-bottom-color: rgba(111,66,193,0.85);
            animation: nnSpin 0.9s linear infinite;
            box-shadow: 0 0 30px rgba(255,43,220,0.18), 0 0 45px rgba(0,194,255,0.12);
          }
          @keyframes nnSpin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: colores.fondo }}>
      <NavbarCliente
        empresa={empresa}
        onCarritoClick={() => setShowCarrito(true)}
        style={{ backgroundColor: colores.header }}
      />

      <div className="nn-page-shell flex-grow-1" style={{ marginTop: "60px" }}>
        {/* ✅ PRE FRONT DE CATEGORÍAS */}
        {mostrarPreFront ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center text-center position-relative"
            style={{
              minHeight: "calc(100vh - 120px)",
              padding: "30px 20px",
              overflow: "hidden",
            }}
          >
            {/* Fondo con logo */}
            {empresa?.logoUrl && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pointerEvents: "none",
                  opacity: 0.08,
                  zIndex: 0,
                }}
              >
                <img
                  src={empresa.logoUrl}
                  alt={empresa.nombre}
                  style={{
                    width: "70%",
                    maxWidth: "420px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: "700px" }}>
              <h1
                style={{
                  fontWeight: 800,
                  fontSize: "2rem",
                  marginBottom: "10px",
                  color: "#111",
                }}
              >
                {empresa?.nombre}
              </h1>

              <p
                style={{
                  fontSize: "1rem",
                  marginBottom: "28px",
                  color: "#444",
                }}
              >
                Elegí una categoría para comenzar
              </p>

              {categorias.length === 0 ? (
                <p className="text-muted">Todavía no hay categorías disponibles.</p>
              ) : (
                <div className="row g-3 justify-content-center">
                  {categorias.map((cat, idx) => (
                    <div key={idx} className="col-6 col-md-4">
                      <button
                        className="btn w-100"
                        onClick={() => handleSeleccionCategoria(cat)}
                        style={{
                          minHeight: "70px",
                          height: "70px",
                          borderRadius: "16px",
                          fontWeight: 700,
                          border: "1px solid rgba(255,255,255,0.18)",
                          color: "#fff",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          padding: "10px 12px",
                          background:
                            "linear-gradient(145deg, #060606 0%, #191919 18%, #4a4a4a 32%, #0b0b0b 48%, #2b2b2b 68%, #000 100%)",
                          boxShadow:
                            "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(255,255,255,0.06), 0 12px 24px rgba(0,0,0,0.28)",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <CategoriaTextoAjustado texto={cat} />
                          </span>
                          <CategoriaBadge cantidad={cantidadPorCategoria[cat] || 0} />
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* 🔹 Carrusel de promociones */}
            {promociones.length > 0 && (
              <div className="mb-2 d-flex justify-content-center">
                <div style={{ maxWidth: "900px", width: "100%", position: "relative" }}>
                  <Carousel interval={3000} indicators={false} controls={promociones.length > 1}>
                    {promociones.map((img, idx) => (
                      <Carousel.Item key={idx}>
                        <div
                          onClick={abrirWhatsappPromos}
                          role="button"
                          title="Consultar promos por WhatsApp"
                          style={{
                            width: "100%",
                            aspectRatio: "16 / 9",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "transparent",
                            borderRadius: "16px",
                            overflow: "hidden",
                            position: "relative",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={img}
                            alt={`Promoción ${idx + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </div>
              </div>
            )}

            {/* 🔍 Filtros */}
            <div className="d-flex flex-column align-items-center mb-3" style={{ maxWidth: "720px", margin: "0 auto" }}>
              <div className="w-100 d-flex justify-content-center mb-2">
                <button
                  className="btn btn-outline-dark btn-sm d-flex align-items-center gap-2"
                  onClick={volverACategorias}
                  type="button"
                >
                  <FaHome />
                  <span>Volver</span>
                </button>
              </div>

              <div className="input-group mb-2" style={{ maxWidth: "400px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar producto..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>

            </div>

            {/* 🛒 Productos */}
            <div className="row g-4 justify-content-center nn-products-grid">
              {productosFiltrados.length === 0 ? (
                <p className="text-center">No se encontraron productos.</p>
              ) : (
                [...productosFiltrados]
                  .sort((a, b) => {
                    const precioA = Number(a.precio || 0);
                    const precioB = Number(b.precio || 0);
                    return ordenPrecio === "asc" ? precioA - precioB : precioB - precioA;
                  })
                  .map((prod) => (
                    <div className={cardColClass} key={prod.id}>
                      <ProductoCard producto={prod} empresa={empresa} />
                    </div>
                  ))
              )}
            </div>
          </>
        )}
      </div>

      <FooterCliente empresa={empresa} />

      <WhatsappButton
        empresaId={empresa.id}
        empresaSlug={empresa.empresaSlug || empresaSlug}
        telefono={empresa.telefono}
        mensaje={`Hola ${empresa.nombre}, necesito hacer una consulta`}
      />

      {showCarrito && (
        <CarritoCliente
          onClose={() => setShowCarrito(false)}
          empresaId={empresa.id}
          empresaSlug={empresa.empresaSlug || empresaSlug}
          telefono={empresa.telefono}
          empresaNombre={empresa.nombre}
        />
      )}

      {/* ✅ MODAL RESERVA */}
      {showReserva && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            background: "rgba(0,0,0,0.65)",
            zIndex: 9999,
            padding: "14px",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowReserva(false)}
        >
          <div
            className="bg-white"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "520px",
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow: "0 18px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div
              className="p-3"
              style={{
                background: "linear-gradient(135deg, rgba(13,110,253,0.10), rgba(255,193,7,0.20))",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <strong style={{ fontSize: "1.05rem" }}>📅 Reservar</strong>
                <button
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setShowReserva(false)}
                  style={{ borderRadius: "12px", fontWeight: 800 }}
                  type="button"
                >
                  Cerrar
                </button>
              </div>
            </div>

            <div className="p-3">
              <p className="mb-2 text-muted">
                (Próximo paso) Acá vamos a mostrar el calendario con fechas disponibles / bloqueadas.
              </p>

              <button
                className="btn btn-success w-100"
                type="button"
                style={{ borderRadius: "14px", fontWeight: 900 }}
                onClick={abrirWhatsappReserva}
              >
                Consultar disponibilidad por WhatsApp
              </button>

              <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                Luego lo cambiamos para que envíe el rango seleccionado.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
